import { createContext } from 'react';

export interface AuthUser {
  readonly sub: string;
  readonly email: string;
  readonly givenName?: string;
  readonly familyName?: string;
  /** User-chosen display handle (Cognito `preferred_username`). */
  readonly preferredUsername?: string;
  /**
   * Cognito user-pool groups from the ACCESS token's `cognito:groups` claim
   * — the same token the agent APIs authorize on, so what the UI gates on
   * matches what the server enforces. Empty when the token carries no
   * group claim. UX-gating only (per-agent eligibility, `agentAccess.ts`);
   * the runtime's server-side check is authoritative.
   */
  readonly groups: readonly string[];
}

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** Force-reload session/attributes from Cognito. */
  /** Re-syncs auth state; resolves `true` when the user is authenticated. */
  readonly refresh: () => Promise<boolean>;
  /** Global sign-out (revokes refresh token) + local cleanup. */
  readonly signOut: () => Promise<void>;
  /** Drop a dead session locally and mark the user unauthenticated, so guards
   *  route back to sign-in. Use when a request reveals the session is gone
   *  (e.g. the agent endpoint returns 401). */
  readonly expireSession: () => void;
}

const noop = async (): Promise<void> => {
  /* replaced by AuthProvider */
};

export const INITIAL_AUTH_STATE: AuthState = {
  status: 'initializing',
  user: null,
  refresh: () => Promise.resolve(false),
  signOut: noop,
  expireSession: () => {
    /* replaced by AuthProvider */
  },
};

export const AuthContext = createContext<AuthState>(INITIAL_AUTH_STATE);
